import orderAddHelper from "../helper/order-route-edit.helper";
import OrderRouteEditPassenger, {
  OrderRouteEditExternalPassenger,
  OrderRouteEditInternalPassenger,
  OrderRouteEditPassengerAddress,
  OrderRouteEditPassengerType,
} from "../types/order-route-edit-passenger";
import OrderRouteEditPassengerSelectOption from "../types/order-route-edit-passenger-select-option";
import OrderRouteEditRouteAddress from "../types/order-route-edit-route-address";
import OrderRouteEditRouteAddressSelectOption from "../types/order-route-edit-route-address-select-option";
import OrderRouteEditRouteItem, {
  OrderRouteEditRouteWaypoint,
  OrderRouteEditRouteWaypointGroup,
  OrderRouteEditRouteWaypointGroupWaypoint,
} from "../types/order-route-edit-route-waypoint";
import OrderRouteEditOrderDetails from "../types/order-route-edit-order-details";
import OrderRouteEditTaxiContract from "../types/order-route-edit-taxi-contract";
import OrderRouteEditPassengerListItem from "../types/order-route-edit-passenger-list-item";
import orderRouteEditHelper from "../helper/order-route-edit.helper";
import OrderRouteEditCargoAddress, {
  OrderRouteEditCargoAddressType,
} from "../types/order-route-edit-cargo-address";
import { CargoCompanyAddressListItem } from "../../../../../common/services/cargo-company/address/list/cargo-company-address-list";
import {
  CargoCompanyPassengerListItem,
  CargoCompanyPassengerListItemAddress,
} from "../../../../../common/services/cargo-company/passenger/list/cargo-company-passenger-list";
import RideSolvedParams, {
  RideSolvedParamsRailyRide,
  RideSolvedParamsRideSeqItem,
  RideSolvedParamsRideWaypoint,
  RideSolvedParamsRideWaypointGroup,
} from "../../../../../common/services/ride/solved/ride-solved-params";
import OrderCargoOrderDetails, {
  OrderCargoOrderDetailsCargoOrderCargoSourceExternal,
  OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
  OrderCargoOrderDetailsCargoOrderRideSeqNode,
  OrderCargoOrderDetailsCargoOrderRideSeqNodeSet,
} from "../../../../../common/services/order/cargo-order/details/order-cargo-order-details";
import OrderUpdateParams, {
  OrderUpdateParamsExternalCargoItemSource,
  OrderUpdateParamsInternalCargoItemSource,
  OrderUpdateSeqGroupNodeRequest,
  OrderUpdateSeqNodeRequest,
} from "../../../../../common/services/order/update/order-update-params";
import CargoAddressType from "../../../../../common/types/cargo-address-type";
import SourceType from "../../../../../common/types/source-type";

const createOrderDetails = (
  prevOrderDetails: OrderCargoOrderDetails,
): OrderRouteEditOrderDetails => {
  return {
    cargoCompanyId:
      prevOrderDetails.cargoOrder.dispatcher.dispatch.cargoCompany.id,
    humanId: prevOrderDetails.cargoOrder.humanId,
    forceAllowToll: prevOrderDetails.forceAllowToll,
  };
};

const createExternalPassengersList = (
  prevOrderDetails: OrderCargoOrderDetails,
): OrderRouteEditPassengerListItem[] => {
  const externalPassengersList: OrderRouteEditPassengerListItem[] = [];

  const externalSubjects =
    prevOrderDetails.cargoOrder.cargoItemsSourceList.filter(
      (s) => s.cargoItemSource.sourceType === SourceType.EXTERNAL,
    );

  if (externalSubjects.length) {
    externalSubjects.forEach((s) => {
      const subjectId = s.cargoSubjectUuid;
      const subjectData =
        s.cargoItemSource as OrderCargoOrderDetailsCargoOrderCargoSourceExternal;

      const externalPassenger: OrderRouteEditPassengerListItem = {
        uuid: subjectId,
        passenger: {
          firstName: subjectData.firstName,
          lastName: subjectData.lastName,
          uuid: subjectId,
          dispatchName: subjectData.dispatchName ?? "",
          externalId: subjectData.externalId ?? "",
          mobile: subjectData.phoneNo,
          type: OrderRouteEditPassengerType.EXTERNAL,
        },
      };

      externalPassengersList.push(externalPassenger);
    });
  }

  return externalPassengersList;
};

const createInternalPassengersList = (
  prevOrderDetails: OrderCargoOrderDetails,
): OrderRouteEditPassengerListItem[] => {
  return prevOrderDetails.passengers.map((p) => {
    const externalId =
      prevOrderDetails.cargoOrder.cargoItemsSourceList.find(
        (item) => item.cargoSubjectUuid === p.uuid,
      )?.cargoItemSource.externalId ?? "";

    return {
      uuid: p.uuid,
      passenger: {
        uuid: p.uuid,
        firstName: p.firstName,
        lastName: p.lastName,
        type: OrderRouteEditPassengerType.INTERNAL,
        externalId,
        addresses: p.passenger.addresses.map((a) => {
          return {
            uuid: a.id,
            displayName: a.displayName,
            latitude: a.lat,
            longitude: a.lon,
            apartmentNumber: a.apartment ?? "",
            houseNumber: a.houseNo ?? "",
            street: a.street ?? "",
            town: a.town ?? "",
            zipCode: a.zipcode ?? "",
          };
        }),
      },
    };
  });
};

const createPassengersList = (
  prevOrderDetails: OrderCargoOrderDetails,
): OrderRouteEditPassengerListItem[] => {
  const externalPassengersList = createExternalPassengersList(prevOrderDetails);
  const internalPassengersList = createInternalPassengersList(prevOrderDetails);

  return [...internalPassengersList, ...externalPassengersList];
};

const matchPassengerFromList = (
  passengerList: OrderRouteEditPassengerListItem[],
  subject: OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
): OrderRouteEditPassengerListItem => {
  return passengerList.find((passenger) => passenger.uuid === subject.uuid)!;
};

const createSingleWaypoint = (
  node: OrderCargoOrderDetailsCargoOrderRideSeqNode,
  passengerList: OrderRouteEditPassengerListItem[],
): OrderRouteEditRouteWaypoint => {
  return {
    uuid: node.meta.uuid,
    address: {
      latitude: node.lat,
      longitude: node.lon,
      displayName: node.displayName,
      uuid: node.meta.uuid,
      apartmentNumber: node.apartment ?? "",
      houseNumber: node.houseNo ?? "",
      street: node.street ?? "",
      town: node.town ?? "",
      zipCode: node.zipCode ?? "",
    },
    date: node.time !== null ? new Date(node.time) : null,
    haltingTimeMinutes: node.haltingTime ? node.haltingTime / 60 : null,
    onboardingPassengerListItems: node.meta.cargoEnter.subjects.map((subject) =>
      matchPassengerFromList(passengerList, subject),
    ),
    outboardingPassengerListItems: node.meta.cargoExit.subjects.map((subject) =>
      matchPassengerFromList(passengerList, subject),
    ),
  };
};

const createWaypointGroup = (
  node: OrderCargoOrderDetailsCargoOrderRideSeqNodeSet,
  passengerList: OrderRouteEditPassengerListItem[],
): OrderRouteEditRouteWaypointGroup => {
  return {
    uuid: node.meta.uuid,
    waypoints: node.seq.map((waypoint) =>
      createSingleWaypoint(waypoint, passengerList),
    ),
  };
};

const createRouteItems = (
  prevOrderDetails: OrderCargoOrderDetails,
  passengerList: OrderRouteEditPassengerListItem[],
): OrderRouteEditRouteItem[] => {
  const routeItems: OrderRouteEditRouteItem[] = [];
  const prevRide = prevOrderDetails.cargoOrder.ride;

  prevRide.seq.forEach((node) => {
    if (orderRouteEditHelper.checkIsSingleNode(node)) {
      routeItems.push(createSingleWaypoint(node, passengerList));
    } else {
      routeItems.push(createWaypointGroup(node, passengerList));
    }
  });

  return routeItems;
};

const createPassengerAddress = (
  responseItem: CargoCompanyPassengerListItemAddress,
): OrderRouteEditPassengerAddress => {
  return {
    latitude: responseItem.lat,
    longitude: responseItem.lon,
    displayName: responseItem.displayName,
    uuid: responseItem.id,
    apartmentNumber: responseItem.apartmentNumber ?? "",
    houseNumber: responseItem.houseNumber ?? "",
    street: responseItem.street ?? "",
    town: responseItem.town ?? "",
    zipCode: responseItem.zipCode ?? "",
  };
};

const createPassenger = (
  responseItem: CargoCompanyPassengerListItem,
): OrderRouteEditPassenger => {
  return {
    uuid: responseItem.user.id,
    firstName: responseItem.user.firstName,
    lastName: responseItem.user.lastName,
    externalId: "",
    addresses: createPassengerAddresses(responseItem.addresses),
    type: OrderRouteEditPassengerType.INTERNAL,
  };
};

const createPassengerAddresses = (
  responsePassengerAddresses: CargoCompanyPassengerListItemAddress[],
): OrderRouteEditPassengerAddress[] => {
  return responsePassengerAddresses.map(createPassengerAddress);
};

const createPassengers = (
  responsePassengers: CargoCompanyPassengerListItem[],
): OrderRouteEditPassenger[] => {
  return responsePassengers.map(createPassenger);
};

const createPassengerSelectOption = (
  passenger: OrderRouteEditPassenger,
): OrderRouteEditPassengerSelectOption => {
  return {
    label: `${passenger.firstName} ${passenger.lastName}`,
    value: passenger,
  };
};

const createPassengerSelectOptions = (
  responsePassengers: CargoCompanyPassengerListItem[],
): OrderRouteEditPassengerSelectOption[] => {
  const passengers = createPassengers(responsePassengers);

  return passengers.map(createPassengerSelectOption);
};

const createCargoAddressType = (
  type: CargoAddressType,
): OrderRouteEditCargoAddressType => {
  switch (type) {
    case CargoAddressType.HOTEL:
      return OrderRouteEditCargoAddressType.HOTEL;
    case CargoAddressType.MEETING_POINT:
      return OrderRouteEditCargoAddressType.MEETING_POINT;
    case CargoAddressType.STATION:
      return OrderRouteEditCargoAddressType.STATION;
  }
};

const createCargoAddress = (
  responseCargoAddress: CargoCompanyAddressListItem,
): OrderRouteEditCargoAddress => {
  return {
    uuid: responseCargoAddress.id,
    displayName: responseCargoAddress.displayName,
    latitude: responseCargoAddress.lat,
    longitude: responseCargoAddress.lon,
    apartmentNumber: responseCargoAddress.apartment ?? "",
    houseNumber: responseCargoAddress.houseNumber ?? "",
    street: responseCargoAddress.street ?? "",
    town: responseCargoAddress.town ?? "",
    zipCode: responseCargoAddress.zipCode ?? "",
    type: createCargoAddressType(responseCargoAddress.cargoAddressType),
  };
};

const createCargoAddresses = (
  responseCargoAddresses: CargoCompanyAddressListItem[],
): OrderRouteEditCargoAddress[] => {
  return responseCargoAddresses.map(createCargoAddress);
};

const createRouteAddressSelectOption = (
  routeAddress: OrderRouteEditRouteAddress,
): OrderRouteEditRouteAddressSelectOption => {
  return {
    label: routeAddress.subText || routeAddress.displayName,
    value: routeAddress,
  };
};

const createRouteAddressesSelectOptions = (
  routeAddresses: OrderRouteEditRouteAddress[],
): OrderRouteEditRouteAddressSelectOption[] => {
  return routeAddresses.map(createRouteAddressSelectOption);
};

const createRailyRideWaypointFromRouteWaypoint = (
  routeWaypoint: OrderRouteEditRouteWaypoint,
) => {
  const newWaypoint: RideSolvedParamsRideWaypoint = {
    lat: routeWaypoint.address!.latitude,
    lon: routeWaypoint.address!.longitude,
    displayName: routeWaypoint.address!.displayName,
    haltingTime: routeWaypoint.haltingTimeMinutes
      ? routeWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      id: routeWaypoint.uuid,
      cargoEnter: {
        subjects: routeWaypoint.onboardingPassengerListItems.map(
          (onboardingPassengerListItem) => {
            const passengerUuid = onboardingPassengerListItem.passenger.uuid;

            return {
              id: passengerUuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeWaypoint.outboardingPassengerListItems.map(
          (outboardingPassengerListItem) => {
            const passengerUuid = outboardingPassengerListItem.passenger.uuid;

            return {
              id: passengerUuid,
            };
          },
        ),
      },
    },
    time: routeWaypoint.date,
  };

  return newWaypoint;
};

const createRailyRideWaypointFromRouteGroupWaypoint = (
  routeGroupWaypoint: OrderRouteEditRouteWaypointGroupWaypoint,
) => {
  const newWaypoint: RideSolvedParamsRideWaypoint = {
    lat: routeGroupWaypoint.address!.latitude,
    lon: routeGroupWaypoint.address!.longitude,
    displayName: routeGroupWaypoint.address!.displayName,
    haltingTime: routeGroupWaypoint.haltingTimeMinutes
      ? routeGroupWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      id: routeGroupWaypoint.uuid,
      cargoEnter: {
        subjects: routeGroupWaypoint.onboardingPassengerListItems.map(
          (onboardingPassengerListItem) => {
            return {
              id: onboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeGroupWaypoint.outboardingPassengerListItems.map(
          (outboardingPassengerListItem) => {
            return {
              id: outboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
    },
    time: null,
  };

  return newWaypoint;
};

const createRailyRidePatch = (
  routeItems: OrderRouteEditRouteItem[],
): (OrderUpdateSeqNodeRequest | OrderUpdateSeqGroupNodeRequest)[] => {
  const patchSequence: (
    | OrderUpdateSeqNodeRequest
    | OrderUpdateSeqGroupNodeRequest
  )[] = [];

  routeItems.forEach((routeItem) => {
    const isWaypoint = orderAddHelper.checkIsRouteItemAWaypoint(routeItem);

    if (isWaypoint) {
      patchSequence.push({
        displayName: routeItem.address?.displayName!,
        haltingTime: routeItem.haltingTimeMinutes
          ? routeItem.haltingTimeMinutes * 60
          : 0,
        lat: routeItem.address?.latitude!,
        lon: routeItem.address?.longitude!,
        time: routeItem.date!,
        meta: {
          cargoEnter: {
            subjects: routeItem.onboardingPassengerListItems.map(
              (passenger) => {
                return {
                  id: passenger.passenger.uuid,
                };
              },
            ),
          },
          cargoExit: {
            subjects: routeItem.outboardingPassengerListItems.map(
              (passenger) => {
                return {
                  id: passenger.passenger.uuid,
                };
              },
            ),
          },
          id: routeItem.uuid,
        },
      });
    } else {
      patchSequence.push({
        seq: routeItem.waypoints.map((waypoint) => {
          return {
            displayName: waypoint.address?.displayName!,
            haltingTime: waypoint.haltingTimeMinutes
              ? waypoint.haltingTimeMinutes * 60
              : 0,
            time: null,
            lat: waypoint.address?.latitude!,
            lon: waypoint.address?.longitude!,
            meta: {
              cargoEnter: {
                subjects: waypoint.onboardingPassengerListItems.map(
                  (passenger) => {
                    return {
                      id: passenger.passenger.uuid,
                    };
                  },
                ),
              },
              cargoExit: {
                subjects: waypoint.outboardingPassengerListItems.map(
                  (passenger) => {
                    return {
                      id: passenger.passenger.uuid,
                    };
                  },
                ),
              },
              id: waypoint.uuid,
            },
          };
        }),
        meta: {
          id: routeItem.uuid,
        },
        sequenceOrder: "ANY",
      });
    }
  });

  return patchSequence;
};

const createEditParams = (
  prevOrderDetails: OrderCargoOrderDetails,
  routeItems: OrderRouteEditRouteItem[],
  passengerList: OrderRouteEditPassengerListItem[],
): OrderUpdateParams => {
  const rv2: Record<
    string,
    | OrderUpdateParamsInternalCargoItemSource
    | OrderUpdateParamsExternalCargoItemSource
  > = {};

  passengerList.forEach((listItem) => {
    const isInternalPassenger = orderRouteEditHelper.checkIsInternalPassenger(
      listItem.passenger,
    );

    if (isInternalPassenger) {
      const listItemPassenger =
        listItem.passenger as OrderRouteEditInternalPassenger;

      const internal: OrderUpdateParamsInternalCargoItemSource = {
        sourceType: "INTERNAL",
        externalId: listItemPassenger.externalId ?? "",
        passengerId: listItemPassenger.uuid,
      };

      rv2[listItem.passenger.uuid] = internal;

      return;
    }
    const listItemPassenger =
      listItem.passenger as OrderRouteEditExternalPassenger;

    const external: OrderUpdateParamsExternalCargoItemSource = {
      sourceType: "EXTERNAL",
      externalId: listItemPassenger.externalId ?? "",
      dispatchName: listItemPassenger.dispatchName ?? "",
      firstName: listItemPassenger.firstName,
      lastName: listItemPassenger.lastName,
      phoneNo: listItemPassenger.mobile,
    };

    rv2[listItem.uuid] = external;
  });

  return {
    rv1: {
      seq: createRailyRidePatch(routeItems),
      sequenceOrder: "STRICT",
      meta: {
        id: prevOrderDetails.cargoOrder.ride.meta.uuid,
      },
    },
    rv2,
    forceAllowToll: prevOrderDetails.forceAllowToll,
    orderId: prevOrderDetails.id,
  };
};

const createRailyRide = (
  routeItems: OrderRouteEditRouteItem[],
): RideSolvedParamsRailyRide => {
  const mainSequence: RideSolvedParamsRideSeqItem[] = [];

  routeItems.forEach((routeItem) => {
    const isWaypoint = orderAddHelper.checkIsRouteItemAWaypoint(routeItem);

    if (isWaypoint) {
      const newWaypoint: RideSolvedParamsRideWaypoint =
        createRailyRideWaypointFromRouteWaypoint(routeItem);

      mainSequence.push(newWaypoint);

      return;
    }

    const sequence: RideSolvedParamsRideWaypoint[] = [];

    routeItem.waypoints.forEach((waypoint) => {
      const newWaypoint: RideSolvedParamsRideWaypoint =
        createRailyRideWaypointFromRouteGroupWaypoint(waypoint);

      sequence.push(newWaypoint);
    });

    const newCargoOrderRide: RideSolvedParamsRideWaypointGroup = {
      sequenceOrder: "ANY",
      seq: sequence,
    };

    mainSequence.push(newCargoOrderRide);
  });

  return {
    seq: mainSequence,
    sequenceOrder: "STRICT",
  };
};

const createSolvedRidesParams = (
  routeItems: OrderRouteEditRouteItem[],
  taxiContract: OrderRouteEditTaxiContract,
): RideSolvedParams => {
  const railyRide = createRailyRide(routeItems);

  return {
    railyRide: railyRide,
    nodeExclusionCollection: {
      exclusions: [
        {
          excludeApproaching: false,
          excludeInside: taxiContract.contractPermitsTollRoads ?? false,
          excludeReturning: false,
          nodeIds: [],
        },
      ],
    },
  };
};

const orderRouteEditFactory = {
  createPassengersList,
  createPassengerSelectOptions,
  createCargoAddresses,
  createRouteAddressSelectOption,
  createRouteAddressesSelectOptions,
  createEditParams,
  createSolvedRidesParams,
  createOrderDetails,
  createRouteItems,
};

export default orderRouteEditFactory;
